// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 — see ADDITIONAL-TERMS.md.

import type { Aspect, PaletteId, PixelSize, ResolvedPalette } from './types.ts';

/** Output pixel dimensions per aspect ratio (720p class — a good size/speed balance). */
export function dimensionsFor(aspect: Aspect): PixelSize {
  switch (aspect) {
    case 'square':
      return { width: 720, height: 720 };
    case 'portrait':
      return { width: 720, height: 1280 };
    case 'landscape':
      return { width: 1280, height: 720 };
  }
}

export const ASPECT_LABEL: Record<Aspect, string> = {
  square: '1:1 · Square',
  portrait: '9:16 · Reels / Stories',
  landscape: '16:9 · YouTube / X',
};

const PALETTES: Record<PaletteId, ResolvedPalette> = {
  studio: {
    bg: ['#17111f', '#0b0b0f'],
    bars: ['#ffb020', '#ff5d8f', '#c057ff'],
    barsDim: '#3a3350',
    text: '#ffffff',
    scrim: 'rgba(10,8,14,0.55)',
  },
  mono: {
    bg: ['#161616', '#050505'],
    bars: ['#ffffff', '#d0d0d0'],
    barsDim: '#3a3a3a',
    text: '#ffffff',
    scrim: 'rgba(0,0,0,0.55)',
  },
  ocean: {
    bg: ['#0b2033', '#05121e'],
    bars: ['#38e8ff', '#2f8bff', '#7a5cff'],
    barsDim: '#26384a',
    text: '#eaf6ff',
    scrim: 'rgba(4,12,22,0.55)',
  },
  sunset: {
    bg: ['#2a1424', '#160a16'],
    bars: ['#ffd166', '#ff7a59', '#ff4d97'],
    barsDim: '#4a2b3a',
    text: '#fff4ee',
    scrim: 'rgba(18,8,14,0.5)',
  },
  forest: {
    bg: ['#0e2018', '#04120c'],
    bars: ['#b6ff5c', '#3ddc84', '#12b3a6'],
    barsDim: '#294034',
    text: '#eefff3',
    scrim: 'rgba(4,14,10,0.5)',
  },
};

export const PALETTE_LABEL: Record<PaletteId, string> = {
  studio: 'Studio',
  mono: 'Mono',
  ocean: 'Ocean',
  sunset: 'Sunset',
  forest: 'Forest',
};

export function resolvePalette(id: PaletteId): ResolvedPalette {
  return PALETTES[id] ?? PALETTES.studio;
}

export const ALL_PALETTES: PaletteId[] = ['studio', 'mono', 'ocean', 'sunset', 'forest'];
export const ALL_ASPECTS: Aspect[] = ['portrait', 'square', 'landscape'];
